import type { SaveVickiMemoryRequest, VickiMemory, VickiMemoryKind } from '@deskworks/shared-types';
import { VICKI_MEMORY_KINDS } from '@deskworks/shared-types';
import { useQuery } from '@tanstack/react-query';
import { Link, useParams } from '@tanstack/react-router';
import { ArrowLeft, BookOpen, History, Pencil, Plus, Trash2, Undo2, X } from 'lucide-react';
import { useState } from 'react';
import { Markdown } from '@/components/markdown';
import { RelativeTime } from '@/components/relative-time';
import { EmptyState, ErrorState, LoadingPage, LoadingRows } from '@/components/states';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Textarea } from '@/components/ui/textarea';
import { api } from '@/lib/api';
import { findAccount, useApiMutation, useMe } from '@/lib/hooks';
import { t } from '@/lib/i18n';
import { qk } from '@/lib/query-keys';
import { cn } from '@/lib/utils';

const KIND_TEXT: Record<VickiMemoryKind, string> = {
  preference: t('Preference'),
  fact: t('Fact'),
  constraint: t('Rule'),
  decision: t('Decision'),
  skill: t('Skill'),
};

/** The page of what VICKI knows, from the Front Desk. */
export function VickiKnowledgePage() {
  const { account: param } = useParams({ from: '/_authed/$account/front-desk/knowledge' });
  const me = useMe();
  const account = me.data ? findAccount(me.data.accounts, param) : undefined;
  if (!account) return <LoadingPage />;
  return (
    <div className="mx-auto w-full max-w-4xl px-4 py-8 sm:px-8">
      <Link to="/$account/front-desk" params={{ account: param }} className="mb-3 inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground">
        <ArrowLeft className="size-3.5" /> {t('Front Desk')}
      </Link>
      <VickiKnowledge accountId={account.id} />
    </div>
  );
}

/**
 * What VICKI knows (docs/features/front-desk.md#memory): what she keeps for good (memory and her own skills), what she
 * noted and hasn't consolidated yet, and her journal. People change, take back or delete anything here. On her page
 * and in the account's settings (`compact`: a section's heading instead of the page's).
 */
export function VickiKnowledge({ accountId, compact }: { accountId: string; compact?: boolean }) {
  const knowledge = useQuery({ queryKey: qk.vickiKnowledge(accountId), queryFn: () => api.frontDesk.knowledge(accountId) });
  const [adding, setAdding] = useState(false);
  if (knowledge.isPending) return compact ? <LoadingRows rows={3} /> : <LoadingPage />;
  if (knowledge.isError) return <ErrorState error={knowledge.error} onRetry={() => void knowledge.refetch()} />;
  const { memories, notes, journal } = knowledge.data;
  const active = memories.filter((m) => m.status === 'active');
  const facts = active.filter((m) => m.kind !== 'skill');
  const skills = active.filter((m) => m.kind === 'skill');
  const revoked = memories.filter((m) => m.status === 'revoked');
  const topics = [...new Set(facts.map((m) => m.topic))];
  return (
    <div>
      <div className="flex flex-wrap items-center gap-3">
        <BookOpen className={cn('text-primary', compact ? 'size-4' : 'size-5')} />
        {compact ? (
          <h2 className="flex-1 text-base font-semibold">{t('What VICKI knows')}</h2>
        ) : (
          <h1 className="flex-1 text-2xl font-semibold tracking-tight">{t('What VICKI knows')}</h1>
        )}
        <Button size="sm" onClick={() => setAdding(true)} disabled={adding}>
          <Plus /> {t('Tell VICKI something')}
        </Button>
      </div>
      <p className="mt-2 text-sm text-muted-foreground">
        {t('What VICKI hears and finds becomes a note first; in her daily round she keeps what matters as memory and writes down how she does things as skills. Change or take back anything here.')}
      </p>
      {adding && <MemoryForm accountId={accountId} onDone={() => setAdding(false)} />}
      <Tabs defaultValue="memory" className="mt-6">
        <TabsList>
          <TabsTrigger value="memory">{t('Memory ({n})', { n: facts.length })}</TabsTrigger>
          <TabsTrigger value="skills">{t('Skills ({n})', { n: skills.length })}</TabsTrigger>
          <TabsTrigger value="notes">{t('Notes ({n})', { n: notes.length })}</TabsTrigger>
          <TabsTrigger value="journal">{t('Journal')}</TabsTrigger>
        </TabsList>
        <TabsContent value="memory" className="mt-4 flex flex-col gap-6">
          {!facts.length && <EmptyState compact title={t('Nothing yet')} description={t('VICKI keeps here what she learns about the business: goals, customers, competition, decisions.')} />}
          {topics.map((topic) => (
            <section key={topic}>
              <h2 className="mb-2 text-xs font-medium tracking-wide text-muted-foreground uppercase">{topic}</h2>
              <div className="flex flex-col gap-2">
                {facts.filter((m) => m.topic === topic).map((m) => (
                  <MemoryCard key={m.id} accountId={accountId} memory={m} />
                ))}
              </div>
            </section>
          ))}
          {revoked.length > 0 && (
            <details>
              <summary className="cursor-pointer text-xs text-muted-foreground">{t('Taken back ({n})', { n: revoked.length })}</summary>
              <div className="mt-2 flex flex-col gap-2 opacity-70">
                {revoked.map((m) => (
                  <MemoryCard key={m.id} accountId={accountId} memory={m} />
                ))}
              </div>
            </details>
          )}
        </TabsContent>
        <TabsContent value="skills" className="mt-4 flex flex-col gap-2">
          {!skills.length && <EmptyState compact title={t('No skills yet')} description={t('How VICKI does recurring work — setting up a kind of desk, a report, when she asks you — she writes down here and improves.')} />}
          {skills.map((m) => (
            <MemoryCard key={m.id} accountId={accountId} memory={m} />
          ))}
        </TabsContent>
        <TabsContent value="notes" className="mt-4 flex flex-col gap-2">
          {!notes.length && <EmptyState compact title={t('No open notes')} description={t('Notes wait here until VICKI’s next round.')} />}
          {notes.map((n) => (
            <Card key={n.id} className="flex items-start gap-3 p-3 text-sm">
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-1.5 text-xs text-muted-foreground">
                  <Badge tone="outline">{KIND_TEXT[n.kind]}</Badge>
                  <span>{n.topic}</span>
                  {n.scopeName && <span>· {n.scopeName}</span>}
                  {n.createdBy && <span>· {n.createdBy}</span>}
                  <span>·</span>
                  <RelativeTime value={n.createdAt} />
                </div>
                <div className="mt-1">{n.text}</div>
                {n.evidence && <div className="mt-1 text-xs text-muted-foreground italic">„{n.evidence}“</div>}
              </div>
              <DiscardNote accountId={accountId} noteId={n.id} />
            </Card>
          ))}
        </TabsContent>
        <TabsContent value="journal" className="mt-4 flex flex-col gap-3">
          {!journal.length && <EmptyState compact title={t('No journal yet')} description={t('VICKI writes a day’s entry in each daily round, and a week’s on Mondays.')} />}
          {journal.map((j) => (
            <Card key={j.id} className="p-4">
              <div className="mb-2 text-xs font-medium text-muted-foreground">
                {j.period === 'week' ? t('Week of {day}', { day: j.periodStart }) : j.periodStart}
              </div>
              <Markdown text={j.text} />
            </Card>
          ))}
        </TabsContent>
      </Tabs>
    </div>
  );
}

function DiscardNote({ accountId, noteId }: { accountId: string; noteId: string }) {
  const discard = useApiMutation(() => api.frontDesk.discardNotes(accountId, [noteId]), { invalidate: [qk.vickiKnowledge(accountId)] });
  return (
    <Button size="icon" variant="ghost" onClick={() => discard.mutate()} loading={discard.isPending} aria-label={t('Discard note')} title={t('Discard note')}>
      <X />
    </Button>
  );
}

function MemoryCard({ accountId, memory: m }: { accountId: string; memory: VickiMemory }) {
  const [editing, setEditing] = useState(false);
  const [history, setHistory] = useState(false);
  const detail = useQuery({ queryKey: qk.vickiMemory(accountId, m.id), queryFn: () => api.frontDesk.memory(accountId, m.id), enabled: history });
  const invalidate = [qk.vickiKnowledge(accountId), qk.vickiMemory(accountId, m.id)];
  const revoke = useApiMutation(() => api.frontDesk.revokeMemory(accountId, m.id), { invalidate, success: t('Taken back.') });
  const restore = useApiMutation(() => api.frontDesk.updateMemory(accountId, m.id, { kind: m.kind, topic: m.topic, title: m.title, text: m.text, alwaysInContext: m.alwaysInContext }), {
    invalidate,
    success: t('Brought back.'),
  });
  const remove = useApiMutation(() => api.frontDesk.deleteMemory(accountId, m.id), { invalidate, success: t('Deleted.') });
  if (editing) return <MemoryForm accountId={accountId} memory={m} onDone={() => setEditing(false)} />;
  return (
    <Card className="p-3 text-sm">
      <div className="flex items-start gap-2">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-1.5">
            <span className="font-medium">{m.title}</span>
            <Badge tone="outline">{KIND_TEXT[m.kind]}</Badge>
            {m.alwaysInContext && <Badge tone="green">{t('Always in mind')}</Badge>}
            {m.scopeName && <span className="text-xs text-muted-foreground">· {m.scopeName}</span>}
          </div>
          <div className="mt-1">{m.kind === 'skill' ? <Markdown text={m.text} /> : <span className="whitespace-pre-wrap">{m.text}</span>}</div>
          <div className="mt-1.5 text-xs text-muted-foreground">
            {m.source === 'person' ? t('Written by a person') : t('By VICKI')} · v{m.version} · <RelativeTime value={m.updatedAt} />
          </div>
        </div>
        <div className="flex shrink-0 gap-0.5">
          <Button size="icon" variant="ghost" onClick={() => setHistory((h) => !h)} aria-label={t('History')} title={t('History')}>
            <History />
          </Button>
          {m.status === 'active' ? (
            <>
              <Button size="icon" variant="ghost" onClick={() => setEditing(true)} aria-label={t('Edit')} title={t('Edit')}>
                <Pencil />
              </Button>
              <Button size="icon" variant="ghost" onClick={() => revoke.mutate()} loading={revoke.isPending} aria-label={t('Take back')} title={t('Take back')}>
                <Undo2 />
              </Button>
            </>
          ) : (
            <Button size="sm" variant="ghost" onClick={() => restore.mutate()} loading={restore.isPending}>
              {t('Bring back')}
            </Button>
          )}
          <Button
            size="icon"
            variant="ghost"
            onClick={() => window.confirm(t('Delete this entry and its history for good?')) && remove.mutate()}
            loading={remove.isPending}
            aria-label={t('Delete')}
            title={t('Delete')}
          >
            <Trash2 />
          </Button>
        </div>
      </div>
      {history && (
        <ul className="mt-2 flex flex-col gap-1 border-t pt-2 text-xs text-muted-foreground">
          {(detail.data?.revisions ?? []).map((r) => (
            <li key={r.version}>
              v{r.version} · {r.status === 'revoked' ? t('taken back') : t('written')} · {r.actor} · <RelativeTime value={r.createdAt} />
              {r.reason ? ` — ${r.reason}` : ''}
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}

function MemoryForm({ accountId, memory, onDone }: { accountId: string; memory?: VickiMemory; onDone: () => void }) {
  const [form, setForm] = useState<SaveVickiMemoryRequest>({
    kind: memory?.kind ?? 'fact',
    topic: memory?.topic ?? 'business',
    title: memory?.title ?? '',
    text: memory?.text ?? '',
    alwaysInContext: memory?.alwaysInContext ?? false,
  });
  const save = useApiMutation(() => (memory ? api.frontDesk.updateMemory(accountId, memory.id, form) : api.frontDesk.addMemory(accountId, form)), {
    invalidate: [qk.vickiKnowledge(accountId), ...(memory ? [qk.vickiMemory(accountId, memory.id)] : [])],
    success: t('VICKI keeps it in mind.'),
    onSuccess: onDone,
  });
  return (
    <Card className="mt-4 flex flex-col gap-3 p-4">
      <div className="flex flex-wrap gap-2">
        <Select value={form.kind} onValueChange={(kind) => setForm({ ...form, kind: kind as VickiMemoryKind })}>
          <SelectTrigger size="sm" className="w-36">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {VICKI_MEMORY_KINDS.map((k) => (
              <SelectItem key={k} value={k}>
                {KIND_TEXT[k]}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Input value={form.topic ?? ''} onChange={(e) => setForm({ ...form, topic: e.target.value.toLowerCase() })} placeholder={t('Topic, e.g. customers')} className="h-8 w-44" />
        <label className="flex items-center gap-2 text-xs text-muted-foreground">
          <Switch checked={!!form.alwaysInContext} onCheckedChange={(on) => setForm({ ...form, alwaysInContext: on })} /> {t('Always in mind')}
        </label>
      </div>
      <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder={t('Title, e.g. Our best customers')} />
      <Textarea value={form.text} onChange={(e) => setForm({ ...form, text: e.target.value })} rows={form.kind === 'skill' ? 10 : 3} placeholder={t('What VICKI should know')} />
      <div className="flex gap-2">
        <Button size="sm" onClick={() => save.mutate()} loading={save.isPending} disabled={!form.title.trim() || !form.text.trim()}>
          {t('Save')}
        </Button>
        <Button size="sm" variant="ghost" onClick={onDone}>
          {t('Cancel')}
        </Button>
      </div>
    </Card>
  );
}
